import React from 'react';
import DataController from '../data/data.controller';
import { getAuthCookie } from '../utils/cookies';

/**
 * True once the session behind DataController has settled — GET /api/user has
 * resolved, or there was no session to fetch in the first place.
 *
 * Nothing eagerly authenticates before the router renders, so on a cold load
 * into a deep link `user.scopes` reads as `[]` for everyone. Any scope check
 * that runs before this flips would read that empty list as "denied" and turn
 * away users who actually hold the scope, so guards must defer until it's true.
 *
 * Safe to call from several routes at once: `authenticate()` short-circuits
 * when the user is already cached, and `fetchUserByToken()` dedupes concurrent
 * calls onto a single in-flight promise. Seeding from that same cache keeps
 * warm client-side navigations from flashing a loading state.
 *
 * @returns {boolean}
 */
export function useSessionHydrated() {
    const isAuthenticated = getAuthCookie() !== 'expired';
    const [hydrated, setHydrated] = React.useState(() => Boolean(DataController.Instance()?.user));

    React.useEffect(() => {
        // No session to hydrate — ProtectedRoute owns the redirect from here.
        if (!isAuthenticated) {
            setHydrated(true);
            return undefined;
        }
        if (DataController.Instance()?.user) {
            setHydrated(true);
            return undefined;
        }
        let cancelled = false;
        DataController.Instance().authenticate().finally(() => {
            if (!cancelled) setHydrated(true);
        });
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated]);

    return hydrated;
}
